/* =========================================================================
 * Kkumi · Cart order summary (sections/main-cart-footer.liquid) — cart v14
 * -------------------------------------------------------------------------
 *   - Promo code / saved offers: members' unused reward codes (/codes) as a
 *     list (Apply · Replace · Remove) + the code field. A free-shipping code is
 *     the exception: it rides beside the order's code (a KKCartCodes rider,
 *     registered from /codes). One on the cart (redeemed in the cart, or the
 *     unused-code reminder's /discount/<code> link) gets its own summary row,
 *     "At checkout" with Remove: Shopify takes it off the shipping there and
 *     may call it not applicable until then, so that is never an issue and
 *     Check out stays on. Where it covers the destination's flat rate, the
 *     shipping panel shows "Free at checkout" and leaves the rate out of the
 *     Total (window.KKCartShipCodes, `kkc:ship-codes`). A saved one that isn't
 *     on the cart has Copy code and a line saying to enter it at checkout, and
 *     typing it in the code field shows a note saying the same, never an error.
 *     One code per order:
 *     replacing asks first (inline confirmation); when the swap raised the
 *     total the shopper is told by how much and offered "Switch back".
 *     A failed apply leaves the previous code in place (KKCartCodes).
 *   - Remove on a summary row / "Continue without code" on a code that no
 *     longer applies.
 *   - Reward codes' summary rows are labelled with the reward's name.
 *   - Check out states: "Updating…" while the line list recalculates,
 *     "Resolve N issues" while the server counted blocking issues,
 *     "Opening secure checkout…" once pressed (error after a timeout if the
 *     page never left), mirrored on the sticky phone bar.
 *   - Store-credit note (members, /store-credit) — never off the total.
 *   - Sticky bar (<1000px): total + Check out, shown only while the summary's
 *     own Check out button is off-screen (IntersectionObserver) and hidden
 *     while the code field has focus. Its height is reserved through
 *     --kkc-bar-h; html.kkc-bar-on moves the club launcher aside meanwhile.
 *   - Polite live announcement when the total changes.
 *   - Flat-rate shipping: the shipping panel (section-kkumi-cart-shipping.js)
 *     sends `kkc:shipping` { ship, cents }; a flat rate is added to the Total,
 *     the sticky bar, the announcement and the store-credit "covers" check.
 *     Free / calculated shipping adds nothing.
 *
 * assets/cart.js re-renders .js-contents after every change, so everything is
 * delegated and re-applied on `cartUpdate`. Copy: data-c-* (section settings).
 * The `disabled` attribute on #checkout is shared with the greenlight guard;
 * this script only ever clears a disable it set itself (data-kkc-dis), or the
 * server's issue-count disable (data-kkc-issues-off) once the only "issues"
 * left are the member's own free-shipping codes.
 * ========================================================================= */
(function () {
  'use strict';
  if (window.__kkcSumInit) return;
  window.__kkcSumInit = true;

  var esc = function (v) { return window.KKEsc(v); };
  var root = null;
  var codes = [];          // /codes rows
  var credit = null;       // cents
  var promoOpen = false;
  var confirm = null;      // { code, from }
  var undo = null;         // { old, from, to }
  var busy = false;        // line list recalculating
  var working = null;      // code being applied / removed
  var starting = false;    // checkout pressed
  var lastTotal = null;
  var shipCents = 0;       // flat-rate shipping in the Total (shopper currency)
  var shipSeen = false;    // the shipping panel has reported at least once

  function c(key, fallback) {
    var k = 'c' + key.charAt(0).toUpperCase() + key.slice(1);
    var v = root && root.dataset[k];
    return v != null && v !== '' ? v : fallback;
  }
  function t(key, fallback, map) {
    var out = esc(c(key, fallback));
    if (map) Object.keys(map).forEach(function (k) { out = out.split('[' + k + ']').join(esc(map[k])); });
    return out;
  }
  function plain(key, fallback, map) {
    var out = c(key, fallback);
    if (map) Object.keys(map).forEach(function (k) { out = out.split('[' + k + ']').join(map[k]); });
    return out;
  }
  function money(cents) { return window.KKMoney.fromCents(cents || 0); }
  function state() { return root ? root.querySelector('[data-kkc-sum-state]') : null; }
  // The order total as shown: the cart total plus any flat-rate shipping.
  function grandTotal() { var st = state(); return (st ? Number(st.dataset.total) || 0 : 0) + shipCents; }
  function grandText() { var st = state(); return shipCents ? money(grandTotal()) : (st ? st.dataset.totalText : ''); }
  function setShip(detail) {
    if (detail) shipSeen = true;
    shipCents = detail && detail.ship === 'flat' ? Number(detail.cents) || 0 : 0;
  }
  function applied() { return window.KKCartCodes ? window.KKCartCodes.applied() : []; }
  // Every code on the cart, applicable or not (the order's code and any free-shipping rider).
  function onCart() { return window.KKCartCodes && window.KKCartCodes.onCart ? window.KKCartCodes.onCart() : []; }
  function lower(s) { return String(s || '').toLowerCase(); }
  function codeRow(code) { return codes.filter(function (cd) { return cd && lower(cd.discount_code) === lower(code); })[0] || null; }
  // Is this one of the member's free-shipping codes? (/codes, or one the points module just redeemed.)
  function isShipCode(code) {
    var cd = codeRow(code);
    return !!((cd && cd.reward_type === 'free_shipping') || (window.KKCartCodes && window.KKCartCodes.isRider && window.KKCartCodes.isRider(code)));
  }
  function isOnCart(code) { return onCart().some(function (c) { return lower(c) === lower(code); }); }
  // The server's "can't be used" boxes for the member's own free-shipping codes: Shopify may report one as
  // not applicable until checkout prices shipping, so it isn't something to fix here.
  function shipOnCart() {
    if (!root) return [];
    return Array.prototype.filter.call(root.querySelectorAll('[data-kkc-bad-code]'), function (el) {
      return isShipCode(el.getAttribute('data-kkc-bad-code'));
    });
  }

  // --- Saved offers ---------------------------------------------------------
  function loadRewards(force) {
    if (!root || root.dataset.member !== 'true' || !window.KKRewards) { renderOffers(); return; }
    var o = { scope: 'member', force: !!force };
    Promise.all([
      window.KKRewards.get('/codes', o).catch(function () { return { codes: [] }; }),
      window.KKRewards.get('/store-credit', o).catch(function () { return { accounts: null }; })
    ]).then(function (res) {
      codes = (res[0] && res[0].codes) || [];
      // The member's free-shipping codes ride beside the order's code (KKCartCodes riders).
      codes.forEach(function (cd) {
        if (cd && cd.reward_type === 'free_shipping' && cd.discount_code && window.KKCartCodes && window.KKCartCodes.addRider) window.KKCartCodes.addRider(cd.discount_code);
      });
      var acc = res[1] && Array.isArray(res[1].accounts) ? res[1].accounts : null;
      if (acc) {
        var shop = window.KKMoneyConfig && window.KKMoneyConfig.shopCurrency;
        var a = acc.filter(function (x) { return x && Number(x.amount) > 0 && (!shop || x.currency === shop); })[0] ||
          acc.filter(function (x) { return x && Number(x.amount) > 0; })[0];
        credit = a ? Math.round(Number(a.amount) * 100) : 0;
      }
      renderAll();
    });
  }

  // Is this saved offer on the order? A free-shipping one counts once it is on the cart (Shopify may call it
  // not applicable until checkout prices shipping).
  function offerOn(cd, onCodes) {
    return onCodes.indexOf(lower(cd.discount_code)) !== -1 || (cd.reward_type === 'free_shipping' && isOnCart(cd.discount_code));
  }

  function offerRow(cd, onCodes) {
    var code = cd.discount_code;
    var on = offerOn(cd, onCodes);
    var hasOther = onCodes.length > 0 && !on;
    var ship = cd.reward_type === 'free_shipping';
    var right;
    if (on) right = '<button type="button" class="kkc-code__b" data-kkc-code-remove="' + esc(code) + '">' + t('remove', 'Remove') + '</button>';
    else if (ship) {
      // Copy, entered at checkout: the click is handled by the global KKCopy button (assets/kkumi-copy.js).
      right = '<button type="button" class="kkc-code__b" data-kk-copy="' + esc(code) + '" data-kk-copied="' + t('copied', 'Copied') + '"><span data-kk-copy-label>' + t('copy', 'Copy code') + '</span></button>';
    } else {
      var spin = working === code ? '<span class="kk-spinner" aria-hidden="true"></span>' : '';
      right = '<button type="button" class="kkc-code__b" data-kkc-offer-apply="' + esc(code) + '"' + (working ? ' aria-disabled="true"' : '') + '>' + spin +
        (hasOther ? t('replace', 'Replace') : t('apply', 'Apply')) + '</button>';
    }
    var sub = on ? t('applied', 'Applied to this order') : '';
    if (!on && cd.expires_at) {
      var d = new Date(Number(cd.expires_at) * (cd.expires_at < 1e12 ? 1000 : 1));
      if (!isNaN(d)) sub = t('expires', 'Use by [date]', { date: d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) });
    }
    if (ship && !on) sub = t('shipNote', 'Enter this code at checkout to make standard shipping free.') + (sub ? ' · ' + sub : '');
    var conf = confirm && confirm.code === code && confirm.from === 'offers' ? confirmHTML(code) : '';
    return '<div class="kkc-code' + (on ? ' kkc-code--on' : '') + '"><div><span class="kkc-code__c">' + esc(code) + '</span> <span class="kkc-tag kkc-tag--soft">' + t('rewardTag', 'Reward') + '</span>' +
      '<span class="kkc-code__l">' + esc(cd.reward_label || cd.product_title || '') + '</span>' + (sub ? '<span class="kkc-code__w">' + sub + '</span>' : '') + '</div>' + right + conf + '</div>';
  }

  function confirmHTML(code) {
    var old = applied()[0] || '';
    return '<div class="kkc-conf" role="group"><div>' + t('conf', 'Only one code per order. [new] will replace [old], which stays in your saved offers.', { new: code, old: old }) + '</div>' +
      '<div class="kkc-conf__row"><button type="button" class="button button--secondary button--small" data-kkc-conf-go="' + esc(code) + '">' + t('confGo', 'Replace') + '</button>' +
      '<button type="button" class="kkc-tbtn" data-kkc-conf-keep>' + t('confKeep', 'Keep [old]', { old: old }) + '</button></div></div>';
  }

  function undoHTML() {
    if (!undo) return '';
    return '<div class="kkc-conf" role="status"><div>' + t('wentUp', 'Your total went from [from] to [to].', { from: money(undo.from), to: money(undo.to) }) + '</div>' +
      '<div class="kkc-conf__row"><button type="button" class="kkc-tbtn" data-kkc-undo="' + esc(undo.old) + '">' + t('undo', 'Switch back to [old]', { old: undo.old }) + '</button></div></div>';
  }

  function renderOffers() {
    if (!root) return;
    var list = root.querySelector('[data-kkc-offers]');
    var chip = root.querySelector('[data-kkc-saved-chip]');
    var confHost = root.querySelector('[data-kkc-promo-conf]');
    var onCodes = applied().map(lower);
    var usable = codes.filter(function (cd) { return cd && cd.discount_code; });
    if (list) {
      var html = usable.map(function (cd) { return offerRow(cd, onCodes); }).join('');
      if (list.innerHTML !== html) list.innerHTML = html;
      list.hidden = !html;
    }
    var avail = usable.filter(function (cd) { return !offerOn(cd, onCodes); }).length;
    if (chip) {
      chip.hidden = !avail;
      chip.textContent = avail ? plain('savedChip', '[count] saved', { count: avail }) : '';
    }
    if (confHost) {
      var ch = (confirm && confirm.from === 'input' ? confirmHTML(confirm.code) : '') + undoHTML();
      if (confHost.innerHTML !== ch) confHost.innerHTML = ch;
    }
    // Reward codes in the summary rows read with the reward's name.
    root.querySelectorAll('[data-kkc-code-label]').forEach(function (el) {
      var code = lower(el.getAttribute('data-kkc-code-label'));
      var cd = codes.filter(function (x) { return lower(x.discount_code) === code; })[0];
      var label = cd && (cd.reward_label || cd.product_title);
      if (label && el.textContent !== label) el.textContent = label;
    });
  }

  function renderCredit() {
    var el = root && root.querySelector('[data-kkc-credit]');
    if (!el) return;
    if (!credit) { el.hidden = true; return; }
    var total = grandTotal();
    var covers = window.KKMoney.toShopCents ? (window.KKMoney.toShopCents(total) || total) <= credit : credit >= total;
    var amt = '<b>' + esc(window.KKMoney.fromShopCents(credit)) + '</b>';
    var html = (covers ? esc(c('creditCovers', 'Your [amount] store credit is enough to cover this entire order. Choose “Store credit” at payment.')) : esc(c('creditHas', 'You have [amount] store credit.'))).split('[amount]').join(amt);
    if (el.innerHTML !== html) el.innerHTML = html;
    el.hidden = false;
  }

  // --- Check out + sticky bar -------------------------------------------------
  // The server's count, less the member's own free-shipping codes it counted (shipOnCart).
  function issues() {
    var st = state();
    var n = st ? Number(st.dataset.issues) || 0 : 0;
    return Math.max(0, n - shipOnCart().length);
  }

  // The member's free-shipping codes on the cart: a summary row each (the code, Remove, the reward's name,
  // "At checkout"), as Shopify takes it off the shipping only at checkout. One Shopify calls not applicable
  // until then comes as a "no longer applies" box: the box becomes the row, its jump button goes, and it
  // leaves the issues box and its count. One Shopify calls applicable has no row (nothing is allocated to
  // the lines), so one is added; a server discount row for it, should Shopify ever list one, becomes the
  // row instead. Re-run after every render (cart.js replaces the server markup).
  function shipRowHTML(code) {
    var cd = codeRow(code);
    return '<span>' + esc(code) + ' <button type="button" class="kkc-x" data-kkc-code-remove="' + esc(code) + '">' + t('remove', 'Remove') + '</button>' +
      '<small>' + esc((cd && (cd.reward_label || cd.product_title)) || c('codeLabel', 'Discount code')) + '</small></span><b>' + t('shipRowAt', 'At checkout') + '</b>';
  }
  function renderShipRows() {
    var rows = root.querySelector('.kkc-rows');
    if (!rows) return;
    var ships = onCart().filter(isShipCode);
    var keyed = function (el) { return lower(el.getAttribute('data-kkc-ship-row') || el.getAttribute('data-kkc-bad-code') || el.getAttribute('data-kkc-code-row')); };
    // A row this script added for a code that has left the cart (the server's boxes go with the re-render).
    Array.prototype.forEach.call(rows.querySelectorAll('[data-kkc-ship-row]:not([data-kkc-bad-code]):not([data-kkc-code-row])'), function (el) {
      if (!ships.some(function (code) { return lower(code) === keyed(el); })) el.remove();
    });
    ships.forEach(function (code) {
      var el = Array.prototype.filter.call(rows.querySelectorAll('[data-kkc-bad-code], [data-kkc-ship-row], [data-kkc-code-row]'), function (x) { return keyed(x) === lower(code); })[0];
      if (!el) {
        var after = rows.querySelectorAll('.kkc-row--disc, [data-kkc-bad-code]');
        var anchor = after.length ? after[after.length - 1] : rows.firstElementChild;
        el = document.createElement('div');
        if (anchor) anchor.insertAdjacentElement('afterend', el); else rows.appendChild(el);
      }
      el.className = 'kkc-row kkc-row--disc kkc-row--ship';
      el.removeAttribute('role');
      el.setAttribute('data-kkc-ship-row', code);
      var html = shipRowHTML(code);
      if (el.innerHTML !== html) el.innerHTML = html;
    });
    Array.prototype.forEach.call(root.querySelectorAll('[data-kkc-jump-code]'), function (b) {
      if (isShipCode(b.getAttribute('data-kkc-jump-code'))) b.hidden = true;
    });
    var box = root.querySelector('.kkc-blockers');
    if (!box || !shipOnCart().length) return;
    var n = issues();
    if (!n) { box.hidden = true; return; }
    var h = box.querySelector('[data-kkc-blockers-h]');
    if (h) h.textContent = n === 1 ? c('blockersOne', '1 item needs attention') : plain('blockersMany', '[count] items need attention', { count: n });
  }

  // The member's free-shipping codes on the cart, for the shipping panel (section-kkumi-cart-shipping.js):
  // { countries, cap } each. One that covers the destination's flat rate is free at checkout, so the panel
  // shows "Free at checkout" and leaves the rate out of the Total. Sent only when it changes.
  var shipCodesKey = '';
  function publishShipCodes() {
    var list = onCart().map(codeRow).filter(function (cd) { return cd && cd.reward_type === 'free_shipping'; }).map(function (cd) {
      return { countries: (cd.ship_countries || []).map(function (x) { return String(x).toUpperCase(); }), cap: Number(cd.max_shipping_cents) || 0 };
    });
    var key = JSON.stringify(list);
    if (key === shipCodesKey) return;
    shipCodesKey = key;
    window.KKCartShipCodes = list;
    document.dispatchEvent(new CustomEvent('kkc:ship-codes', { detail: list }));
  }

  function renderCheckout() {
    var btn = document.getElementById('checkout');
    var barBtn = root.querySelector('[data-kkc-bar-co]');
    var st = state();
    var empty = st ? st.dataset.empty === 'true' : false;
    var n = issues();
    var label, short, spin = false, off = false;
    if (busy || working) { label = c('updating', 'Updating…'); short = label; spin = true; off = true; }
    else if (starting) { label = c('opening', 'Opening secure checkout…'); short = c('openingShort', 'Opening…'); spin = true; off = true; }
    else if (n) {
      label = n === 1 ? c('resolveOne', 'Resolve 1 issue to check out') : plain('resolve', 'Resolve [count] issues to check out', { count: n });
      short = plain('resolveShort', 'Fix [count]', { count: n });
      off = true;
    } else { label = c('co', 'Check out'); short = c('coShort', 'Check out'); }
    if (empty) off = true;
    [[btn, label], [barBtn, short]].forEach(function (pair) {
      var b = pair[0];
      if (!b) return;
      var html = (spin ? '<span class="kk-spinner" aria-hidden="true"></span>' : '') + esc(pair[1]);
      if (b.innerHTML !== html) b.innerHTML = html;
      b.toggleAttribute('aria-busy', spin);
      if (b === barBtn) b.setAttribute('aria-label', label);
      if (off) {
        if (!b.disabled) { b.disabled = true; b.dataset.kkcDis = '1'; }
      } else if (b.dataset.kkcDis || b.hasAttribute('data-kkc-issues-off')) {
        // Ours, or the server's issue-count disable when the only issues were free-shipping codes.
        delete b.dataset.kkcDis;
        b.removeAttribute('data-kkc-issues-off');
        if (!b.dataset.glDisabled) b.disabled = false;
      }
    });
    var sum = root.querySelector('[data-kkc-sum]');
    if (sum) sum.setAttribute('aria-busy', String(!!(busy || working)));
  }

  // The bar duplicates the summary's Check out, so it only shows while that
  // button is off-screen. cart.js can replace the summary markup, so the
  // observed node is re-checked on every render.
  var coSeen = false;      // summary Check out on screen
  var coObs = null, coEl = null;
  function watchCheckout() {
    if (!('IntersectionObserver' in window)) return;
    var el = root.querySelector('.kkc-sum__co');
    if (el === coEl) return;
    if (!coObs) {
      coObs = new IntersectionObserver(function (entries) {
        var seen = entries[entries.length - 1].isIntersecting;
        if (seen !== coSeen) { coSeen = seen; renderBar(); }
      }, { rootMargin: '0px 0px -24px 0px' });
    }
    if (coEl) coObs.unobserve(coEl);
    coEl = el;
    coSeen = false;
    if (el) coObs.observe(el);
  }

  function renderBar() {
    var bar = root.querySelector('[data-kkc-bar]');
    if (!bar) return;
    watchCheckout();
    var st = state();
    var empty = !st || st.dataset.empty === 'true';
    var show = !empty && !coSeen && window.innerWidth < 1000 && !root.classList.contains('kkc-kbd');
    bar.hidden = !show;
    document.documentElement.classList.toggle('kkc-bar-on', show);
    var totalEl = bar.querySelector('[data-kkc-bar-total]');
    var labelEl = bar.querySelector('[data-kkc-bar-label]');
    var text = grandText();
    if (st && totalEl && totalEl.textContent !== text) totalEl.textContent = text;
    var lbl = busy ? c('totalUpdating', 'Total · updating') : c('totalLabel', 'Total');
    if (labelEl && labelEl.textContent !== lbl) labelEl.textContent = lbl;
    document.documentElement.style.setProperty('--kkc-bar-h', show ? bar.offsetHeight + 'px' : '0px');
  }

  function renderTotal() {
    var el = root && root.querySelector('[data-kkc-total-v]');
    var text = grandText();
    if (el && text && el.textContent !== text) el.textContent = text;
  }

  function announceTotal() {
    if (!state()) return;
    var total = grandTotal();
    if (lastTotal != null && total !== lastTotal) {
      var live = root.querySelector('[data-kkc-live]');
      if (live) live.textContent = plain('announce', 'Order total is now [total]', { total: grandText() });
    }
    lastTotal = total;
  }

  function restorePromo() {
    var d = root.querySelector('[data-kkc-promo]');
    if (d && promoOpen && !d.open) d.open = true;
  }

  function renderAll() {
    if (!root) return;
    restorePromo();
    // cart.js replaced .js-contents, so the "Earns about N pts" tracker is a
    // fresh (skeleton) element the earn-points script hasn't seen yet.
    if (window.KkumiEarnPoints && window.KkumiEarnPoints.scan) window.KkumiEarnPoints.scan(root);
    renderOffers();
    renderTotal();
    renderCredit();
    renderShipRows();
    renderCheckout();
    renderBar();
    publishShipCodes();
  }

  // --- Code actions ------------------------------------------------------------
  function setPromoErr(msg) {
    var el = root.querySelector('[data-kkc-promo-err]');
    var input = root.querySelector('#KkcPromoI');
    if (el) { el.textContent = msg || ''; el.hidden = !msg; }
    if (input) { if (msg) input.setAttribute('aria-invalid', 'true'); else input.removeAttribute('aria-invalid'); }
  }

  // The member typed their own free-shipping code: not an error, a note under the field saying it is theirs
  // and where to use it, with Copy (the global KKCopy button, assets/kkumi-copy.js). The field clears.
  function setPromoHint(typed) {
    var el = root.querySelector('[data-kkc-promo-hint]');
    if (!el) return;
    if (!typed) { el.hidden = true; el.innerHTML = ''; return; }
    var code = (codeRow(typed) || {}).discount_code || typed;
    var icon = window.KKIcons ? window.KKIcons.html('truck') : '';
    el.innerHTML = '<span class="kkc-hint__i" aria-hidden="true">' + icon + '</span>' +
      '<p><b>' + t('shipTyped', '[code] is your free shipping code.', { code: code }) + '</b> ' + t('shipTypedText', 'Enter it at checkout to redeem.') +
      ' <button type="button" class="kkc-tbtn" data-kk-copy="' + esc(code) + '" data-kk-copied="' + t('copied', 'Copied') + '"><span data-kk-copy-label>' + t('shipTypedCopy', 'Copy Code') + '</span></button></p>';
    el.hidden = false;
    var input = root.querySelector('#KkcPromoI');
    if (input) input.value = '';
  }

  function apply(code, from) {
    if (!window.KKCartCodes || working) return;
    var before = state() ? Number(state().dataset.total) || 0 : 0;
    var old = applied()[0] || null;
    working = code;
    confirm = null;
    undo = null;
    setPromoErr('');
    renderAll();
    // A saved reward code goes through the points module, which adds a
    // free-item reward's item first (Shopify won't take the code without it).
    var viaPoints = window.KKCartPoints && window.KKCartPoints.owns(code);
    var run = viaPoints
      ? window.KKCartPoints.applySaved(code).then(function (r) {
          if (!r || r.picking) return { ok: true, picking: true, state: null };
          return { ok: r.ok, message: r.message, state: window.KKCartCodes.cart() };
        })
      : window.KKCartCodes.apply(code);
    run.then(function (r) {
      working = null;
      if (r.picking) return; // the points module's chooser takes it from here
      if (!r.ok) {
        promoOpen = true;
        var msg = r.message || plain('errInapplicable', '“[code]” can’t be used on this cart.', { code: code });
        setTimeout(function () { setPromoErr(msg); }, 0);
        return;
      }
      var after = r.state && r.state.total_price != null ? r.state.total_price : before;
      if (old && lower(old) !== lower(code) && after > before) undo = { old: old, from: before, to: after };
      if (from === 'input') { var input = root.querySelector('#KkcPromoI'); if (input) input.value = ''; }
    }).catch(function () {
      working = null;
      setTimeout(function () { setPromoErr(plain('errRequest', 'We couldn’t apply “[code]”. Please try again.', { code: code })); }, 0);
    }).then(function () {
      renderAll();
      document.dispatchEvent(new CustomEvent('kkc:codes-changed'));
    });
  }

  function requestApply(code, from) {
    code = String(code || '').trim();
    if (!code) return;
    setPromoHint('');
    var cur = applied();
    var isOn = cur.some(function (x) { return lower(x) === lower(code); });
    if (isOn) return;
    // The member's own free-shipping code: on the order already, or entered at checkout (a note, not an error).
    if (isShipCode(code)) {
      setPromoErr('');
      if (isOnCart(code)) { var input = root.querySelector('#KkcPromoI'); if (input) input.value = ''; }
      else setPromoHint(code);
      return;
    }
    if (cur.length) { confirm = { code: code, from: from }; promoOpen = true; renderAll(); return; }
    apply(code, from);
  }

  function remove(code, btn) {
    if (!window.KKCartCodes || working) return;
    working = code;
    undo = null;
    if (btn && window.KKBtn) window.KKBtn.busy(btn, '');
    renderCheckout();
    window.KKCartCodes.remove(code).catch(function () {
      setPromoErr(c('errRemove', 'We couldn’t remove the code. Please try again.'));
    }).then(function () {
      working = null;
      renderAll();
      document.dispatchEvent(new CustomEvent('kkc:codes-changed'));
    });
  }

  // --- Events ------------------------------------------------------------------------
  function bind() {
    root.addEventListener('click', function (e) {
      var el = e.target.closest ? e.target : null;
      if (!el) return;
      var x;
      if ((x = el.closest('[data-kkc-offer-apply]'))) { if (x.getAttribute('aria-disabled') !== 'true') requestApply(x.getAttribute('data-kkc-offer-apply'), 'offers'); return; }
      if ((x = el.closest('[data-kkc-conf-go]'))) { apply(x.getAttribute('data-kkc-conf-go'), confirm ? confirm.from : 'offers'); return; }
      if ((x = el.closest('[data-kkc-conf-keep]'))) { confirm = null; renderAll(); return; }
      if ((x = el.closest('[data-kkc-undo]'))) { var o = x.getAttribute('data-kkc-undo'); undo = null; apply(o, 'undo'); return; }
      if ((x = el.closest('[data-kkc-code-remove]'))) { e.preventDefault(); remove(x.getAttribute('data-kkc-code-remove'), x); return; }
      if ((x = el.closest('[data-kkc-jump]'))) {
        var line = document.getElementById('CartItem-' + x.getAttribute('data-kkc-jump'));
        if (line) { line.scrollIntoView({ behavior: 'smooth', block: 'center' }); line.focus({ preventScroll: true }); }
        return;
      }
      if ((x = el.closest('[data-kkc-jump-code]'))) {
        var want = x.getAttribute('data-kkc-jump-code');
        var box = Array.prototype.filter.call(root.querySelectorAll('[data-kkc-bad-code]'), function (b) { return b.getAttribute('data-kkc-bad-code') === want; })[0];
        if (box) { box.setAttribute('tabindex', '-1'); box.scrollIntoView({ behavior: 'smooth', block: 'center' }); box.focus({ preventScroll: true }); }
      }
    });
    root.addEventListener('toggle', function (e) {
      if (e.target && e.target.matches && e.target.matches('[data-kkc-promo]')) promoOpen = e.target.open;
    }, true);
    root.addEventListener('submit', function (e) {
      var f = e.target.closest ? e.target.closest('[data-kkc-promo-form]') : null;
      if (!f) return;
      e.preventDefault();
      var input = f.querySelector('input');
      requestApply(input ? input.value : '', 'input');
    });
    // Mobile keyboard: hide the sticky bar while the code field has focus.
    root.addEventListener('focusin', function (e) { if (e.target && e.target.id === 'KkcPromoI') { root.classList.add('kkc-kbd'); renderBar(); } });
    root.addEventListener('focusout', function (e) { if (e.target && e.target.id === 'KkcPromoI') { root.classList.remove('kkc-kbd'); renderBar(); } });

    // Check out pressed (page button or sticky bar): "Opening secure checkout…".
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || form.id !== 'cart') return;
      var sub = e.submitter;
      if (!sub || sub.name !== 'checkout' || e.defaultPrevented) return;
      starting = true;
      var errEl = root.querySelector('[data-kkc-co-err]');
      if (errEl) errEl.hidden = true;
      setTimeout(renderCheckout, 0);
      setTimeout(function () {
        if (!starting || document.visibilityState === 'hidden') return;
        starting = false;
        if (errEl) { errEl.textContent = c('coError', 'We couldn’t open checkout. Please try again.'); errEl.hidden = false; }
        renderCheckout();
      }, 12000);
    });
    window.addEventListener('pageshow', function (e) { if (e.persisted) { starting = false; renderAll(); } });
    window.addEventListener('resize', renderBar);
    // Re-applied on every shipping render (cart.js just re-rendered the Total
    // from Liquid, or the shopper picked another country).
    document.addEventListener('kkc:shipping', function (e) {
      var first = !shipSeen;
      setShip(e.detail);
      if (!root) return;
      renderTotal();
      renderCredit();
      renderBar();
      // The first report is the page settling, not a change to announce.
      if (first) lastTotal = state() ? grandTotal() : null;
      else announceTotal();
    });
    document.addEventListener('kkc:busy', function (e) { busy = !!(e.detail && e.detail.busy); renderCheckout(); renderBar(); });
    document.addEventListener('kkc:rewards-changed', function (e) {
      if (e.detail && e.detail.credit != null) credit = e.detail.credit;
      loadRewards(true);
    });
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () { setTimeout(function () { renderAll(); announceTotal(); }, 0); });
    }
  }

  function init() {
    var el = document.querySelector('[data-kkc-summary]');
    if (!el || el === root) return;
    root = el;
    bind();
    // The shipping panel's script runs first on the cart page.
    setShip(window.KKCartShip);
    lastTotal = state() ? grandTotal() : null;
    renderAll();
    loadRewards(false);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', function () { root = null; init(); });
})();
